import { useTranslation } from 'react-i18next';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Constants from 'style/constants';
import { AlarmPriority } from 'shared/components/alarm-card/enums/alarm-priority';
import { AlarmStatus } from 'shared/components/alarm-card/enums/alarm-status';
import { AlarmType } from 'shared/components/alarm-card/enums/alarm-type';
import getPriorityColor from 'shared/components/alarm-card/utils/get-priority-color';
import AlarmIcon from 'shared/components/icons/alarm-icon';
import GoogleMapsIcon from 'shared/components/icons/google-maps-icon';
import { getAlarmTimeForAlarmCard } from 'shared/utils/date-utils';

import '../alarm-card.scss';

type AcceptedByUser = {
  id: string;
  userFullName: string;
};

type ExternalLocationCoordinates = {
  longitude: number;
  latitude: number;
};

type Props = {
  priority: AlarmPriority;
  status: AlarmStatus;
  type: AlarmType;
  translatedType: string;
  nearbyText?: string;
  externalLocationCoordinates?: ExternalLocationCoordinates;
  message?: string;
  statusLastChangedAt?: string;
  reportedByFullName: string;
  acceptedByUsers: AcceptedByUser[];
};

const EmergencyAlarmCard = ({
  priority,
  status,
  type,
  translatedType,
  nearbyText,
  externalLocationCoordinates,
  message,
  statusLastChangedAt,
  reportedByFullName,
  acceptedByUsers,
}: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('alarmCard');

  const showInMapClicked = (coordinates: ExternalLocationCoordinates): void => {
    window.open(`https://maps.google.com?q=${coordinates.latitude},${coordinates.longitude}`);
  };

  return (
    <div className={`alarm-card-container ${getPriorityColor(priority)} status-${status}`}>
      <div className="alarm-card-content">
        <div className={`content-names-row`}>
          <div className="notification-icon-background">
            <AlarmIcon type={type} />
          </div>
          <div className="content-names">
            <Typography variant="body1">{reportedByFullName}</Typography>
          </div>
        </div>
        <div className={`content-information-row`}>
          <Typography variant="subtitle1">{translatedType}</Typography>
          <Typography variant="subtitle1">{getAlarmTimeForAlarmCard(statusLastChangedAt, t('dateDisplayYesterday'))}</Typography>
        </div>

        {message && (
          <div className={`content-information-row`}>
            <Typography variant="subtitle1">{message}</Typography>
          </div>
        )}

        {nearbyText && (
          <div className={`content-nearby-information-row`}>
            <Typography variant="subtitle1">{t('nearby')}</Typography>
            <Typography variant="body1">{nearbyText}</Typography>
          </div>
        )}

        {externalLocationCoordinates && (
          <div className={`content-nearby-information-row`}>
            {!nearbyText && <Typography variant="subtitle1">{t('nearby')}</Typography>}
            <Link
              className={`content-nearby-information-row open-google-maps`}
              onClick={() => showInMapClicked(externalLocationCoordinates)}
            >
              <GoogleMapsIcon
                sx={{
                  fontSize: '16.5px',
                }}
              />
              <Typography variant="body1">{t('lookAtMap')}</Typography>
            </Link>
          </div>
        )}

        {status !== AlarmStatus.Reported && acceptedByUsers.length > 0 && (
          <>
            <Typography variant="h6">{t('acceptedBy')}</Typography>
            <div className="accepted-by-users-container">
              {acceptedByUsers.map((user) => (
                <div className="status-container" key={`accepted-by-user-${user.id}`}>
                  <div className="avatar-container">
                    <Typography variant="overline" sx={{ color: Constants.Colors.onSurfaceVariant }} className="name">
                      {`${user.userFullName?.split(' ').shift()?.charAt(0)}${user.userFullName?.split(' ').pop()?.charAt(0)}`}
                    </Typography>
                  </div>
                  <Typography variant="overline" sx={{ color: Constants.Colors.onSurfaceVariant }} className="name">
                    {user.userFullName}
                  </Typography>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmergencyAlarmCard;
